import React, { useEffect, useState } from "react";
import styled from "styled-components";
import PlanPriceBlue from "./PlanPriceBlue";
import { constants } from "../redux/constants";
import { getTimeFromPlanTypeD, goTo } from "../utilities/utils";
import { useDispatch, useSelector } from "react-redux";
import { updateShopObject } from "../redux/actions/shopActions";
import { plansWithoutTopUp } from "../utilities/constants";
import { Trans, useTranslation } from "react-i18next";
import { useI18next } from "gatsby-plugin-react-i18next";
import { RootState } from "../redux/reducers/rootReducer";
import { promocodesWithoutFiveDollarsOff } from "../utilities/constants";
import { Plan } from "../types/Plan";
import { PlansObject } from "../types/PlansObject";
import { Script } from "gatsby";

interface PlanSelectCardProps {
  enableESIM: boolean;
  isWeekly: boolean;
  rightSide: boolean;
  plan: Plan;
  index: number;
}

interface NewPlanSelectListProps {
  currency: string;
  country: string;
  plans?: PlansObject;
  enableShipping: boolean;
  enableESIM: boolean;
  initialPlanType: string;
  availablePlanTypes: string[];
}

const NewPlanSelectList: React.FC<NewPlanSelectListProps> = (
  props: NewPlanSelectListProps
) => {
  const dispatch = useDispatch();
  const dispatchShop = (obj) => dispatch(updateShopObject(obj) as any);
  const [globalFontFamily, setGlobalFontFamily] = useState("Poppins");

  let routeDetails = useSelector((state: RootState) => {
    return state.route;
  });

  let shopDetails = useSelector((state: RootState) => {
    return state.shop;
  });

  const isActivationPage = routeDetails.routeString === "activation";
  const isCanada = props.country === constants.CANADA;

  const [availablePlanTypes, setAvailablePlanTypes] = useState([
    "postpaid",
    "prepaid",
  ]);
  const [selectedPlanType, setSelectedPlanType] = useState(
    constants.POSTPAID_PLAN
  );
  const country = isCanada ? constants.CANADA : constants.USA;
  const currency = isCanada
    ? constants.CANADA_CURRENCY
    : constants.USA_CURRENCY;
  const isUSA = !isCanada;
  const countryColor = isCanada ? "#0494CA" : "#82C36D";
  const lightCountryColor = isCanada ? "#ECFAFF" : "#E3FADA";
  const [leftPlans, setLeftPlans] = useState([]);
  const [rightPlans, setRightPlans] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("left");
  const [isWeekly, setIsWeekly] = useState(false);

  // Special Boxing Day Plans
  const [specialPlans, setSpecialPlans] = useState([]);

  const leftSideTitle = isUSA
    ? "5G Plans"
    : "Up to 1,000 minutes of International Calling";
  const leftSideSubtitle = isUSA
    ? "Premium Networks at Affordable Prices"
    : "4G/5G Network";
  const rightSideTitle =
    isWeekly && isUSA
      ? "Weekly Plans"
      : "Up to 1,000 minutes of International Calling";
  const rightSideSubtitle =
    isWeekly && isUSA ? "Ideal for Travellers" : `4G/5G Network`;
  const leftSideExtraText = isCanada
    ? "UNLIMITED Canada-wide Calling, Texting & International Messaging"
    : "UNLIMITED USA-wide Calling, Texting & International Messaging";
  const rightSideExtraText = isWeekly
    ? `UNLIMITED ${
        isCanada ? "Canada" : "USA"
      }-wide Calling, Texting & International Messaging`
    : `UNLIMITED ${
        isCanada ? "Canada" : "USA"
      }-wide Calling, Texting & International Messaging`;
  const [isMobile, setIsMobile] = useState(false);
  const { language } = useI18next();
  const { t } = useTranslation();
  const [pageWidth, setPageWidth] = useState(0);
  const bothPlanTypesAvailable = availablePlanTypes.length === 2;
  const [outageModalVisible, setOutageModalVisible] = useState(false);
  const [containerCardWidth, setContainerCardWidth] = useState({
    full: 0,
    half: 0,
  });

  const promo2Check = shopDetails.isPostpaid
    ? shopDetails.promocode
    : shopDetails.prepaidPromocode;

  const doNotShowFiveDollarsOff =
    promocodesWithoutFiveDollarsOff.includes(promo2Check);

  const leftIconName = "5G";
  const rightIconName = isCanada ? "forest" : "usa-icon";
  const leftText = (
    <WhiteCopyText>
      <Trans
        defaults="Get on the <CCT>best network</CCT> for less & unlock all PhoneBox benefits!"
        components={{
          CCT: (
            <CursiveCopyText
              style={{ whiteSpace: "nowrap" }}
              color={countryColor}
            />
          ),
        }}
      />
    </WhiteCopyText>
  );

  const rightText = isWeekly ? (
    <WhiteCopyText style={{ whiteSpace: "pre-line" }}>
      <Trans
        defaults={`Visiting ${
          isCanada ? "Canada" : "the USA"
        } for a <CCT>short time?</CCT> We've got you covered.`}
        components={{
          CCT: <CursiveCopyText color={"#36C2CF"} />,
        }}
      />
    </WhiteCopyText>
  ) : (
    <WhiteCopyText>
      <Trans>
        Stay Connected with your friends & family abroad with up to 1,000
        minutes of International Calling!
      </Trans>
    </WhiteCopyText>
  );

  // language useEffect
  useEffect(() => {
    if (language === "ko") {
      setGlobalFontFamily("Pretendard");
    } else {
      setGlobalFontFamily("Poppins");
    }
  }, [language]);

  const shouldImgAppear = (plans: Plan[], specialImg = false) => {
    if (pageWidth > 1400 && !bothPlanTypesAvailable && plans.length % 2 === 0) {
      return false;
    }
    if (specialImg && pageWidth > 1200 && pageWidth < 1400) {
      return true;
    }
    if (plans.length % 2 === 0 && pageWidth > 1200 && !bothPlanTypesAvailable) {
      return true;
    }
    if (plans.length % 2 === 0 && pageWidth > 1200 && bothPlanTypesAvailable) {
      return false;
    }
    if (plans.length % 2 === 0 && pageWidth <= 992) {
      return false;
    }
    if (
      plans.length % 2 === 1 &&
      pageWidth < 1200 &&
      pageWidth > 992 &&
      plans.length <= 3
    ) {
      return false;
    }
    return true;
  };

  const offerCardWidth =
    containerCardWidth.full / 5 > 270 ? containerCardWidth.full / 5 : 265;

  const onSIMClick = (plan: Plan) => {
    //   dispatchShop({ noTopUpFee: plansWithoutTopUp.includes(plan.planId) })
    dispatchShop({ planId: plan.planId });
    dispatchShop({ currency: currency });
    dispatchShop({ data: plan.data });
    dispatchShop({ plan_fee: plan.plan_fee });
    dispatchShop({ pfCode: plan.pfCode });
    dispatchShop({ country: country });
    dispatchShop({ planCarrier: plan.carrier });
    dispatchShop({ shipping_country: country });
    dispatchShop({ planTypeD: plan.planTypeD });
    dispatchShop({
      isPostpaid:
        selectedPlanType.toLowerCase() ===
        constants.POSTPAID_PLAN.toLowerCase(),
    });
    dispatchShop({ editMode: false });
    if (isCanada) {
      let plans =
        selectedPlanType.toLowerCase() === constants.PREPAID_PLAN.toLowerCase()
          ? props.plans.Canada.Prepaid
          : props.plans.Canada.Postpaid;
      dispatchShop({ plans: plans });
    } else {
      let plans =
        selectedPlanType.toLowerCase() === constants.PREPAID_PLAN.toLowerCase()
          ? props.plans.USA.Prepaid
          : [];
      dispatchShop({ plans: plans });
    }
  };

  const onESIMClick = (plan: Plan) => {
    onSIMClick(plan);
    dispatchShop({ esim: true });
    dispatchShop({ delivery_needed: false });
    if (plan.plintronPlanId) {
      dispatchShop({ us_plintron_plan_id: plan.plintronPlanId });
    }
    if (plan.subscriptionId) {
      dispatchShop({ us_plan_i_subscription: plan.subscriptionId });
    }
    goTo("/plan-info");
  };

  const onRegularSIMClick = (plan: Plan) => {
    onSIMClick(plan);
    dispatchShop({ esim: false });
    dispatchShop({ delivery_needed: props.enableShipping });
    if (plan.plintronPlanId) {
      dispatchShop({ us_plintron_plan_id: plan.plintronPlanId });
    }
    if (plan.subscriptionId) {
      dispatchShop({ us_plan_i_subscription: plan.subscriptionId });
    }
    goTo("/plan-info");
  };

  const onClickTryEsimNow = () => {
    let URL = new URLSearchParams(window.location.search);
    let bn = URL.get("bn");
    let i_rep = URL.get("i_rep");
    if (bn || i_rep) {
      window.location.assign(
        `https://esim.gophonebox.com/?bn=${bn}&i_rep=${i_rep}`
      );
    } else {
      window.location.assign(`https://esim.gophonebox.com/`);
    }
  };

  useEffect(() => {
    const handleWindowResize = () => {
      if (window.innerWidth < 768) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
      setPageWidth(window.innerWidth);

      if (window.innerWidth < 768) {
        setContainerCardWidth({
          full: 540,
          half: 245,
        });
        return;
      }
      if (window.innerWidth < 992) {
        setContainerCardWidth({
          full: 720,
          half: 335,
        });
        return;
      }
      if (window.innerWidth < 1200) {
        setContainerCardWidth({
          full: 960,
          half: 455,
        });
        return;
      }
      if (window.innerWidth < 1400) {
        setContainerCardWidth({
          full: 1140,
          half: 545,
        });
        return;
      }

      setContainerCardWidth({
        full: 1320,
        half: 635,
      });
    };
    handleWindowResize();
    window.addEventListener("resize", handleWindowResize);

    return () => window.removeEventListener("resize", handleWindowResize);
  }, [pageWidth]);

  useEffect(() => {
    let mainPlans = [];
    let sidePlans = [];

    // USA plans
    if (props.country === constants.USA) {
      // US only has prepaid types for now
      setAvailablePlanTypes(["prepaid"]);
      setSelectedPlanType("prepaid");

      props.plans.USA.Prepaid.forEach((plan) => {
        if (plan.planTypeD === "7" || plan.planTypeD === "15") {
          sidePlans.push(plan);
        } else {
          mainPlans.push(plan);
        }
      });
      setIsWeekly(true);
    } else if (props.country === constants.CANADA) {
      // CANADA plans

      // check # of postpaid and prepaid plans
      // this determines whether the main switch is available or not
      // and whether we should switch to prepaid as default or not
      let planTypes = [];
      if (props.plans.Canada.Postpaid.length > 0) {
        planTypes.push("postpaid");
      }
      if (props.plans.Canada.Prepaid.length > 0) {
        planTypes.push("prepaid");
      }
      setAvailablePlanTypes(planTypes);

      if (selectedPlanType.toLowerCase() === "prepaid") {
        props.plans.Canada.Prepaid.forEach((plan) => {
          // if (plan.planTypeD === '90') {
          //   sidePlans.push({
          //     carrier: ""
          //   } as Plan)
          //   sidePlans.push(plan);
          // } else i
          if (plan.planTypeD === "7" || plan.planTypeD === "90") {
            sidePlans.push(plan);
          } else {
            mainPlans.push(plan);
          }
        });
        setIsWeekly(true);
      } else if (selectedPlanType.toLowerCase() === "postpaid") {
        const _specialPlans = props.plans.Canada.Postpaid.filter((p) =>
          [2012, 2013].includes(p.planId)
        );
        setSpecialPlans(_specialPlans);

        const remainingPlans = props.plans.Canada.Postpaid.filter(
          (p) => ![2012, 2013].includes(p.planId)
        );
        remainingPlans.forEach((plan) => {
          if (
            plan.carrier.toLowerCase() === "rogers" ||
            plan.carrier.toLowerCase() === "r"
          ) {
            mainPlans.push(plan);
          } else {
            sidePlans.push(plan);
          }
        });
        setIsWeekly(false);
      }
    }
    // set left and right plans
    setLeftPlans(mainPlans);
    setRightPlans(sidePlans);
    // left and right are set, determine selected category and number of cards

    if (
      mainPlans.length === 0 ||
      (props.initialPlanType === constants.PREPAID_PLAN &&
        sidePlans.length !== 0)
    ) {
      setSelectedCategory("right");
    } else {
      setSelectedCategory("left");
    }
  }, [props.country, selectedPlanType]);

  useEffect(() => {
    if (
      props.plans.Canada.Postpaid.length === 0 ||
      props.initialPlanType === constants.PREPAID_PLAN
    ) {
      setSelectedPlanType("prepaid");
    } else {
      setSelectedPlanType("postpaid");
    }
  }, [props.plans]);

  useEffect(() => {
    var serverError = window.localStorage.getItem("serverError");
    if (serverError === "403") {
      setOutageModalVisible(true);
    }
  }, []);

  const PlanSelectCard: React.FC<PlanSelectCardProps> = (
    cardProps: PlanSelectCardProps
  ) => {
    const isNinetyDaysPlan = cardProps.plan.planTypeD === "90";
    const isWeeklyPlan = cardProps.isWeekly && cardProps.rightSide;
    const isUnlimited =
      cardProps.plan.plan_fee === 55 && props.country === constants.USA;
    const isDisabled = false;
    const altColor = isNinetyDaysPlan
      ? "#36C2CF"
      : isWeeklyPlan
      ? "#36C2CF"
      : countryColor;
    const altLightColor = isNinetyDaysPlan
      ? "#ECFAFF"
      : isWeeklyPlan
      ? "#ECFAFF"
      : lightCountryColor;

    // no more 90 days plan
    // if (cardProps.plan.carrier === "") {
    //   return (
    //     <div style={{ width: offerCardWidth, display: 'inline-block', padding: 17, marginTop: 20, verticalAlign: 'top', marginRight: 10, marginLeft: 10, textAlign: "left" }}>
    //       {/* <div style={{backgroundColor: "#E93F25", color: "#fff", fontFamily: "Damion", width: "fit-content", padding: "0px 4px 4px 4px", borderRadius: "5px", height: "18px", lineHeight: "16px" }}>new!</div> */}
    //       <div style={{ color: altColor, fontSize: "18px", fontFamily: globalFontFamily, fontWeight: 600 }}><Trans>90 Days Plan</Trans> •</div>
    //       <div style={{ whiteSpace: "nowrap" }}><Trans>UNLIMITED Canada-wide Calling</Trans></div>
    //       <div style={{ whiteSpace: "nowrap" }}><Trans>& International Messaging</Trans></div>
    //     </div>
    //   )
    // }

    const time = getTimeFromPlanTypeD(
      cardProps.plan.planTypeD,
      selectedPlanType.toLowerCase() === constants.PREPAID_PLAN.toLowerCase()
    );

    return (
      <div
        className="plan-select-card"
        style={{
          width: offerCardWidth,
          display: "inline-block",
          padding: 10,
          marginTop: 20,
          verticalAlign: "top",
          marginRight: 10,
          marginLeft: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <PlanPriceBlue
            price={cardProps.plan.plan_fee}
            timeUnit={time}
            currency={isCanada ? "CAD" : "USD"}
            color={altColor}
            justifyContentStart
            koreanLanguage={language === "ko"}
          />
          <div style={{ display: "flex", justifyContent: "center" }}>
            <DataLabel color={altColor} lightColor={altLightColor}>
              {isUnlimited ? t("Unlimited") + "\u002A" : t(cardProps.plan.data)}
            </DataLabel>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            marginTop: 10,
            justifyContent: "space-between",
          }}
        >
          <RegularSimButton
            color={altColor}
            onClick={() => onRegularSIMClick(cardProps.plan)}
            disabled={isDisabled}
          >
            <Trans>Regular SIM</Trans>
          </RegularSimButton>
          {props.enableESIM && (
            <ESimButton
              color={altColor}
              onClick={() => onESIMClick(cardProps.plan)}
              disabled={isDisabled}
            >
              eSIM
            </ESimButton>
          )}
        </div>
        {isUnlimited && (
          <div
            style={{
              textAlign: "left",
              fontSize: 10,
              color: countryColor,
              paddingTop: 4,
            }}
          >
            <Trans>{`\u002ASpeed reduces after `}</Trans>
            {cardProps.plan.data}.
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div
        style={{
          display: "flex",
          flexDirection:
            containerCardWidth.full <= 960 ? "column-reverse" : "row",
          justifyContent: "space-between",
          fontFamily: globalFontFamily,
        }}
      >
        <div style={{ display: "flex", flexDirection: "row" }}>
          {availablePlanTypes.includes("postpaid") && isCanada && (
            <PlanTag
              color={countryColor}
              selected={selectedPlanType.toLowerCase() === "postpaid"}
              onClick={() => {
                setSelectedPlanType("postpaid");
                setSelectedCategory("left");
              }}
            >
              <Trans>Monthly Plans</Trans>
              {selectedPlanType.toLowerCase() === "postpaid" && (
                <PlanTagArrow color={countryColor} />
              )}
            </PlanTag>
          )}
          {availablePlanTypes.includes("prepaid") && (
            <PlanTag
              color={countryColor}
              selected={selectedPlanType.toLowerCase() === "prepaid"}
              onClick={() => setSelectedPlanType("prepaid")}
            >
              <Trans>Prepaid Plans</Trans>
              {selectedPlanType.toLowerCase() === "prepaid" && (
                <PlanTagArrow color={countryColor} />
              )}
            </PlanTag>
          )}
        </div>
        {/* do not show banners away from activation page */}
        {/* {isCanada && !isActivationPage &&
          <div style={{ marginBottom: containerCardWidth.full <= 960 ? 20 : 0 }}>
            {selectedPlanType.toLowerCase() === 'prepaid' &&
              <>
                <NewEsimBanner onClick={onClickTryEsimNow}>
                  <img className="new-esim-img" src={"../images/plans/top-banner.png"} />
                </NewEsimBanner>
              </>
            }
          </div>
        } */}
        <div
          style={{
            marginBottom: 20,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* <NewEsimBanner onClick={onClickTryEsimNow}>
            {pageWidth <= 500 ? (
              <img
                className="new-esim-img"
                src={"../images/plans/top-banner.png"}
              />
            ) : (
              <img
                className="new-esim-img"
                src={"../images/plans/top-banner.png"}
              />
            )}
          </NewEsimBanner> */}
          <img
            className="new-esim-img"
            src={"/images/posters/top-banner.png"}
            style={{
              marginTop: "10px",
              width: "100%",
              maxWidth: "450px",
              height: "auto",
              objectFit: "contain",
              borderRadius: "10px",
            }}
          />
          <img
            className="new-esim-img"
            src={"/images/plans/banner-1.png"}
            style={{
              marginTop: "10px",
              width: "100%",
              maxWidth: "450px",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </div>
      </div>
      {pageWidth < 1200 && rightPlans.length > 0 && leftPlans.length > 0 && (
        <div style={{ display: "flex", flexDirection: "row", marginTop: 20 }}>
          <PlanToggle
            selected={selectedCategory === "left"}
            color={countryColor}
            onClick={() => setSelectedCategory("left")}
          >
            <div>
              <Trans>{leftSideTitle}</Trans>
            </div>
          </PlanToggle>
          <PlanToggle
            selected={selectedCategory === "right"}
            color={countryColor}
            onClick={() => setSelectedCategory("right")}
          >
            <div>
              <Trans>{rightSideTitle}</Trans>
            </div>
          </PlanToggle>
        </div>
      )}
      <>
        <div
          style={{
            position: "relative",
            display: "flex",
            flexDirection: pageWidth >= 1200 ? "row" : "column",
            justifyContent: "space-between",
            marginTop: 20,
          }}
        >
          {leftPlans.length > 0 &&
            (pageWidth >= 1200 || selectedCategory === "left") && (
              <ContainerCard
                width={containerCardWidth}
                style={{ height: "fit-content" }}
              >
                <div
                  style={{
                    height: "60px",
                    marginBottom: isMobile ? "50px" : "0px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      fontSize: "16px",
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 600 }}>
                        <Trans>{leftSideTitle}</Trans>
                      </span>
                      {" • "}
                      <Trans>{leftSideSubtitle}</Trans>
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontFamily: globalFontFamily,
                      height: 24,
                    }}
                  >
                    <Trans>{leftSideExtraText}</Trans>
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  {leftPlans.map((plan, i) => (
                    <PlanSelectCard
                      key={i}
                      plan={plan}
                      index={i}
                      rightSide={false}
                      isWeekly={false}
                      enableESIM={props.enableESIM}
                    />
                  ))}
                  {shouldImgAppear(leftPlans) && (
                    <div
                      style={{
                        display: "inline-block",
                        height: "130px",
                        width: offerCardWidth,
                        verticalAlign: "top",
                        marginTop: 20,
                        position: "relative",
                      }}
                    >
                      <img
                        src={"/images/plans/people-left.png"}
                        height="130px"
                      ></img>
                      <div
                        style={{
                          backgroundColor: countryColor,
                          position: "absolute",
                          height: "100%",
                          width: 224,
                          marginLeft: 20,
                          top: 0,
                          opacity: 0.5,
                          borderRadius: "10px",
                        }}
                      ></div>
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          width: 191,
                          color: "#fff",
                          marginLeft: 36,
                        }}
                      >
                        <div
                          style={{
                            textAlign: "center",
                            padding: 10,
                            verticalAlign: "center",
                          }}
                        >
                          <img
                            src={`/images/plans/${leftIconName}.png`}
                            style={{ display: "inline-block", height: 40 }}
                          ></img>
                          <div>{leftText}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* {shouldFiveDollarsOffAppear() &&
                  <div style={{ display: 'inline-block', height: '130px', width: offerCardWidth, verticalAlign: 'top', marginTop: 20, position: 'relative' }}>
                    <img src={"/images/plans/five-dollars-off.png"} height='130px'></img>
                  </div>
                } */}
                </div>
              </ContainerCard>
            )}
          {(rightPlans.length > 0 || specialPlans.length > 0) &&
            (pageWidth >= 1200 || selectedCategory === "right") && (
              <ContainerCard
                width={containerCardWidth}
                style={{ height: "fit-content" }}
              >
                <div
                  style={{
                    height: "60px",
                    marginBottom: isMobile ? "50px" : "0px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      fontSize: "16px",
                      color: "#000",
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 600 }}>
                        <Trans>{rightSideTitle}</Trans>
                      </span>
                      {" • "}
                      <Trans>{rightSideSubtitle}</Trans>
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontFamily: globalFontFamily,
                      color: "#000",
                    }}
                  >
                    <Trans>{rightSideExtraText}</Trans>
                  </div>
                </div>
                <div
                  style={{ textAlign: "center" }}
                  className="plans-container"
                >
                  {specialPlans.length &&
                  selectedPlanType.toLowerCase() ===
                    constants.POSTPAID_PLAN.toLowerCase() ? (
                    <div
                      className="special-limited-offers-container"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "15px",
                      }}
                    >
                      <img
                        src="/images/NewcomersBenefitTitle.png"
                        style={{ marginTop: "20px" }}
                      />
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "20px",
                          flexWrap: "wrap",
                        }}
                      >
                        {specialPlans.map((plan, i) => (
                          <div
                            key={i}
                            style={{
                              backgroundImage:
                                "url('/images/SpecialOfferFrame.png')",
                              backgroundSize: "contain",
                              backgroundRepeat: "no-repeat",
                              backgroundPosition: "center",
                              padding: "20px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <PlanSelectCard
                              plan={plan}
                              index={i}
                              rightSide={false}
                              isWeekly={false}
                              enableESIM={props.enableESIM}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {rightPlans.map((plan, i) => (
                    <PlanSelectCard
                      key={i}
                      plan={plan}
                      index={i}
                      rightSide={true}
                      isWeekly={isWeekly}
                      enableESIM={props.enableESIM}
                    />
                  ))}
                  {shouldImgAppear(rightPlans) && (!isWeekly || !isCanada) && (
                    <div
                      style={{
                        width: offerCardWidth,
                        display: "inline-block",
                        height: "130px",
                        verticalAlign: "top",
                        marginTop: 20,
                        position: "relative",
                      }}
                    >
                      <img
                        src={"/images/plans/people-right.png"}
                        height="130px"
                      ></img>
                      <div
                        style={{
                          backgroundColor: isWeekly ? "#36C2CF" : countryColor,
                          position: "absolute",
                          height: "100%",
                          width: 223,
                          marginLeft: 22,
                          top: 0,
                          opacity: 0.5,
                          borderRadius: "10px",
                        }}
                      ></div>
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          width: 191,
                          color: "#fff",
                          marginLeft: 36,
                        }}
                      >
                        <div
                          style={{
                            textAlign: "center",
                            padding: 10,
                            paddingLeft: 10,
                            verticalAlign: "center",
                          }}
                        >
                          <img
                            src={`/images/plans/${rightIconName}.png`}
                            style={{ display: "inline-block", height: 40 }}
                          ></img>
                          <div>{rightText}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  {!shouldImgAppear(rightPlans, true) &&
                    !isMobile &&
                    (!isWeekly || !isCanada) && (
                      <div
                        style={{
                          width: 540,
                          display: "inline-block",
                          height: "fit-content",
                          verticalAlign: "top",
                          position: "relative",
                          marginTop: 20,
                        }}
                      >
                        <img
                          src={"/images/plans/horizontal-altered.png"}
                          style={{
                            height: "128px",
                            width: "100%",
                            overflow: "hidden",
                          }}
                        ></img>
                        <div
                          style={{
                            backgroundColor: isWeekly
                              ? "#36C2CF"
                              : countryColor,
                            position: "absolute",
                            height: "100%",
                            width: "100%",
                            top: 0,
                            opacity: 0.5,
                            borderRadius: "10px",
                          }}
                        ></div>
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            width: 540,
                            color: "#fff",
                            marginLeft: 48,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              paddingLeft: 12,
                              paddingTop: 40,
                              alignContent: "center",
                            }}
                          >
                            <img
                              src={`/images/plans/${rightIconName}.png`}
                              style={{
                                display: "inline-block",
                                height: 45,
                                marginRight: 10,
                              }}
                            ></img>
                            <div style={{ paddingRight: 100 }}>{rightText}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  {/* {shouldFiveDollarsOffAppear() &&
                  <div style={{ display: 'inline-block', height: '130px', width: offerCardWidth, verticalAlign: 'top', marginTop: 20, position: 'relative' }}>
                    <img src={"/images/plans/five-dollars-off.png"} height='130px'></img>
                  </div>
                } */}
                </div>
              </ContainerCard>
            )}
        </div>
      </>
    </Layout>
  );
};

const Layout = styled.div``;

const ContainerCard = styled.div<{ width: { full: number; half: number } }>`
  border-radius: 10px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.05), 0 6px 12px 0 rgba(0, 0, 0, 0.09);
  padding: 30px;
  width: ${(props) => props.width.full}px;
  display: inline-block;
  &:not(:only-child) {
    width: ${(props) => props.width.half}px;
  }
  @media (max-width: 1200px) and (min-width: 768px) {
    width: ${(props) => props.width.full}px !important;
  }
  @media (max-width: 768px) {
    width: 100% !important;
  }
`;

const PlanTag = styled.div<{ selected: boolean; color: string }>`
  border-style: solid;
  border-radius: 10px;
  border-color: ${(props) => props.color};
  border-width: 1px;
  text-decoration: none;
  padding: 5px;
  text-align: center;
  font-size: 20px;
  color: ${(props) => (props.selected ? "#FFF" : props.color)};
  background-color: ${(props) => (props.selected ? props.color : "#FFF")};
  width: fit-content;
  height: fit-content;
  padding: 12px 24px;
  display: block;
  font-weight: 600;
  cursor: pointer;
  margin-right: 12px;
  position: relative;
`;

const BestDealEverBanner = styled.div<{ isMobile: boolean; width: string }>`
  border-radius: 10px;
  text-decoration: none;
  text-align: center;
  font-size: 20px;
  color: #000000;
  background-color: #fce9e8;
  width: ${(props) => props.width};
  padding: 12px ${(props) => (props.isMobile ? "48px" : "0px")} 12px 24px;
  display: block;
  font-weight: 600;
  position: relative;
  margin-bottom: 12px;
`;

const UnmatchedPlansBanner = styled.div<{ isMobile: boolean; width: string }>`
  border-radius: 10px;
  text-decoration: none;
  text-align: left;
  font-size: 18px;
  color: #000000;
  background-color: #ffd500;
  width: ${(props) => props.width};
  padding: 13.5px 24px;
  display: block;
  font-weight: 600;
  position: relative;
`;

const EsimBanner = styled.div<{ width: string; fontFamily: string }>`
  border-radius: 10px;
  width: ${(props) => props.width};
  background-color: #0494ca;
  color: #fff;
  font: normal normal bold 20px/20px ${(props) => props.fontFamily};
  align-items: center;
  padding: 7px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  cursor: pointer;
`;

const NewEsimBanner = styled.div`
  cursor: pointer;

  .new-esim-img {
    border-radius: 10px;
    width: 100%;
    height: auto;
    max-width: 450px;
    object-fit: contain;
  }
`;

const PlanTagArrow = styled.div<{ color: string }>`
  position: absolute;
  bottom: -8px;
  height: 16px;
  width: 16px;
  background-color: ${(props) => props.color};
  transform: rotateY(0deg) rotate(45deg);
`;

const PlanToggle = styled.div<{ selected: boolean; color: string }>`
  border-radius: 25px;
  font-weight: 600;
  text-align: center;
  font-size: 16px;
  width: fit-content;
  padding: 10px 20px;
  margin-right: 12px;
  color: ${(props) => (props.selected ? "#FFF" : props.color)};
  background-color: ${(props) => (props.selected ? props.color : "#FFF")};
  cursor: pointer;
`;

const DataLabel = styled.div<{ color: string; lightColor: string }>`
  color: ${(props) => props.color};
  background-color: ${(props) => props.lightColor};
  padding: 10px;
  height: fit-content;
  border-radius: 10px;
  width: fit-content;
  font-weight: 600;
  font-size: 20px;
`;

const RegularSimButton = styled.div<{ color: string; disabled: boolean }>`
  border-style: solid;
  border-radius: 10px;
  border-color: ${(props) => (props.disabled ? "lightgrey" : props.color)};
  border-width: 1px;
  text-decoration: none;
  padding: 5px;
  text-align: center;
  color: ${(props) => (props.disabled ? "lightgrey" : props.color)};
  width: 160px;
  display: block;
  font-weight: 600;
  cursor: pointer;
`;

const ESimButton = styled.div<{ color: string; disabled: boolean }>`
  margin-left: 10px;
  border-radius: 10px;
  text-decoration: none;
  padding: 5px;
  text-align: center;
  color: #fff;
  background-color: ${(props) => (props.disabled ? "lightgrey" : props.color)};
  width: 60px;
  display: block;
  font-weight: 600;
  cursor: pointer;
  clip-path: polygon(77% 0, 100% 35%, 100% 100%, 0 100%, 0 0);
`;

const WhiteCopyText = styled.p`
  display: inline-block;
  text-align: left;
  font-size: 12px;
  color: #fff;
  margin-bottom: 0px !important;
`;

const CursiveCopyText = styled.span<{ color: string }>`
  background-color: ${(props) => props.color};
  border-radius: 5px;
  font-family: Damion;
  padding: 0px 5px;
  font-size: 16px;
`;

const EsimBannerHighlight = styled.span`
  color: red;
  background-color: #fff;
  border-radius: 10px;
  padding: 0px 5px;
`;

export default NewPlanSelectList;
